import { Grid, List, Modal, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { isAddress } from 'viem'
import { normalize } from 'viem/ens'

import profileSetup from '@/assets/webp_converted/createProfileHeader.webp'
import Lamina1Logo from '@/components/Lamina1Logo'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import AddressDisplay from '@/components/User/AddressDisplay'
import { CONTRACT_COLLECTOR_ADDRESS, FREE_MIN_BALANCE, ID_BOT } from '@/constants/blockchain'
import L1PaymentCollector from '@/contracts/L1NSPaymentCollector'
import { useChainFunctions, useChainValues } from '@/hooks/useChain'
import useL1NS from '@/hooks/useL1NS'
import useLocalStorage from '@/hooks/useLocalStorage'
import useProfilePath from '@/hooks/useProfilePath'
import { useAuth } from '@/plugins/auth'
import { l1IdSubnetWagmiConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'

import identitygif from '../../../assets/webp_converted/identity.webp'
import { useErrorMessages } from '../../../constants/content'
import {
  convertToEpochSeconds,
  ErrorMessageDisplay,
  formatBalance,
  post,
  UserIdentity,
} from '../../../utils/utils'
import BackButton from '../../Buttons/BackButton'
import RootButton from '../../Buttons/RootButton'
import ErrorMessage from '../../ErrorMessage'
import ConfirmFreeUsername from '../../Modals/Usernames/ConfirmFreeUsername'
import ConfirmPremiumUsername from '../../Modals/Usernames/ConfirmPremiumUsername'
import { AtIcon, CheckedIcon, InvalidIcon } from '../../SpecialInputText'
import SpecialInputTile from '../../SpecialInputTile'
import Timer from '../../Timer'
import { MAX_HANDLE_LENGTH, MIN_HANDLE_LENGTH } from '../rules'
import { FreeTextArea, useFreeUsernameText } from './free'
import { PremiumDetails, PremiumTextArea, usePremiumUsernameText } from './premium'
import classes from './Username.module.css'

const IdentityNameRules: React.FC = () => {
  const theme = useMantineTheme()
  return (
    <Stack style={{ color: theme.colors.notification[0], gap: '0px' }}>
      <List withPadding style={{ color: theme.colors.notification[0] }}>
        <List.Item>
          <Trans i18nKey="components.identity.userValidation.length">
            All L1 usernames must be between {{ min: MIN_HANDLE_LENGTH }} and{' '}
            {{ max: MAX_HANDLE_LENGTH - 4 }} characters long
          </Trans>
        </List.Item>
        <List.Item>
          <Trans i18nKey="components.identity.userValidation.charsWarning">
            Limited support for special characters (!, ;, *, etc.)
          </Trans>
        </List.Item>
        <List.Item>
          <Trans i18nKey="components.identity.userValidation.info">
            For more information, see&nbsp;
            <a
              href="https://docs.lamina1.com/docs/L1-Username-Guidelines--Recommendations"
              target="_blank"
              rel="noreferrer"
            >
              L1 Username Guidelines & Recommendations
            </a>
          </Trans>
        </List.Item>
      </List>
    </Stack>
  )
}

const IdentityStringSizeRule: React.FC<{ countChars: number }> = ({ countChars }) => {
  const theme = useMantineTheme()
  return (
    <Stack style={{ display: 'flex', flexDirection: 'row', gap: 0 }}>
      <Text
        style={{
          color:
            countChars > MAX_HANDLE_LENGTH - 4 || countChars < MIN_HANDLE_LENGTH
              ? theme.colors.notification[0]
              : theme.colors.title[0],
        }}
      >
        {countChars}
      </Text>
      <Text
        style={{
          color:
            countChars > MAX_HANDLE_LENGTH - 4
              ? theme.colors.notification[0]
              : theme.colors.textAndIcon[0],
        }}
      >
        /{MAX_HANDLE_LENGTH - 4}
      </Text>
    </Stack>
  )
}

const Component: React.FC = () => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, pathname } = useLocation()
  const premium = useMemo(() => pathname === '/premiumusername', [pathname])
  const { address } = useAuth()
  const errorMessages = useErrorMessages()
  const profilePath = useProfilePath(address)

  // Is renewal
  const isRenewal = useMemo(() => state?.isRenewal === true, [state])

  // Get username passed via state for re purchase
  const selectedUsername = useMemo(() => (state?.username as string) || '', [state])

  const { premiumLabel, premiumModalTitle, premiumTitleArea, premiumCheckAvailabilityButtonText } =
    usePremiumUsernameText(isRenewal)
  const { freeLabel, freeModalTitle, freeTitleArea, freeCheckAvailabilityButtonText } =
    useFreeUsernameText()

  const { readContract } = useChainFunctions(l1NativeWagmiConfig)
  const { balance } = useChainValues(l1NativeWagmiConfig.id)
  const { balance: balanceId } = useChainValues(l1IdSubnetWagmiConfig.id)

  const { confirmHandleAvailability } = useL1NS()

  const [identityUsername, setIdentityUsername] = useState<string>('')
  const [tested, setTested] = useState<string>('')
  const label = premium ? premiumLabel : freeLabel
  const [message, setMessage] = useState<string | JSX.Element>(label)
  const submitting = useRef<boolean>(false)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const usernameAvailable = useRef<boolean>(false)
  const [countChars, setCountChars] = useState<number>(0)
  const [borderStatus, setBorderStatus] = useState<'default' | 'success' | 'error'>('default')
  const [displayUsernameRules, setDisplayUsernameRules] = useState<boolean>(false)
  const [isTimerOn, setIsTimerOn] = useState<boolean>(false)
  const enoughBalance = useRef<boolean>(true)
  const [minBalance, setMinBalance] = useState<number | undefined>()
  const [expiryTimestamp, setExpiryTimestamp] = useState<number>(10)
  const [userIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)
  const [createProfile, setCreateProfile] = useState<boolean>(!premium)
  const [, setFirstLogin] = useLocalStorage<boolean>(`firstLogin-${address}`)

  // Confirm Identity Modal
  const [opened, { open, close }] = useDisclosure(false)
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)

  // Error Message Modal
  const [openedErrorModal, { open: openErrorModal, close: closeErrorModal }] = useDisclosure(false)
  const [errorMessage, setErrorMessage] = useState<ErrorMessageDisplay>({
    message: '',
    details: '',
    report: true,
  })

  // Display balance - premium
  const displayBalance = useMemo(() => {
    if (balance) {
      return parseFloat(balance.fullValue).toFixed(4)
    }
    return '0'
  }, [balance])

  // if pathname is /usersettings change buttons
  const isUserSettings = useMemo(() => {
    if (state) {
      return state.pathname === '/usersettings' || state.pathname === '/profile'
    }
    return false
  }, [state])

  const skip = useCallback(() => {
    setFirstLogin(false)
    if (state)
      navigate(state.pathname, {
        state: {
          from: 'username',
        },
      })
    else navigate(profilePath)
  }, [navigate, state, profilePath])

  const setAUsername = useCallback(() => {
    setFirstLogin(false)
    setCreateProfile(false)
  }, [])

  const success = useCallback(() => {
    close()
    if (state)
      navigate(state.pathname, {
        state: {
          from: 'username',
        },
      })
    else navigate(profilePath)
  }, [close, state, profilePath])

  const closeModal = useCallback(() => {
    submitting.current = false
    setIsConfirmed(false)
    close()
  }, [close])

  const handleConfirmation = (confirmed: boolean) => {
    setIsConfirmed(confirmed)
    // timer is off when user confirms identity
    setIsTimerOn(false)
  }

  // Timer timeout
  const timeout = useCallback(() => {
    // if timer resets, closes modals
    closeErrorModal()
    close()
  }, [close, closeErrorModal])

  // premium
  const checkBalance = useCallback(async () => {
    if (!identityUsername) {
      return false
    }

    // Get price from contract
    try {
      const price = await readContract({
        address: CONTRACT_COLLECTOR_ADDRESS as `0x${string}`,
        abi: L1PaymentCollector,
        functionName: 'price',
        args: [normalize(identityUsername)],
      })
      const premiumMinBalance = parseFloat(formatBalance(price))
      setMinBalance(premiumMinBalance)

      // Check if user has enough balance
      return parseFloat(balance.fullValue) >= premiumMinBalance
    } catch (error: any) {
      setErrorMessage({
        message: errorMessages.rpcConnection,
        details: `${t(
          'components.identity.errorMessageCheckBalance',
          'To check if you have enough balance, we need to read the price to pay from the Lamina1 blockchain. Please try again later.'
        )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
          error: error.message,
        })}`,
      })
      openErrorModal()
      return false
    }
  }, [balance, errorMessages.rpcConnection, readContract, identityUsername, openErrorModal])

  // submit username to L1ns
  const submitFree = useCallback(async () => {
    submitting.current = true

    // Check if address has enough balance on L1ID. If not request to faucet.
    if (!address) {
      setMessage(t('components.identity.noAddrReconnect', 'No address found. Reconnect to hub.'))
      setBorderStatus('error')
      submitting.current = false
      return
    }
    if (parseFloat(balanceId.fullValue) < FREE_MIN_BALANCE) {
      // Post Faucet to request L1ID tokens
      try {
        const retval = await post(`${ID_BOT}/faucet`, JSON.stringify({ address }))
        if (retval?.txHash === '') {
          // assume the time string is of the time "23h50m15s"
          const time = (retval.ttl as string)
            .replace('s', ' seconds')
            .replace('h', ' hours ')
            .replace('m', ' minutes ')

          setErrorMessage({
            message: errorMessages.idBotRateLimit(time),
            details: retval.error,
            report: false,
          })
          openErrorModal()
          submitting.current = false
          return
        }
      } catch (error: any) {
        setErrorMessage({
          message: errorMessages.idBotConnection,
          details: `${t(
            'components.identity.errorMessageFree',
            'The Lamina1 ID Bot is being used to request tokens from the faucet. Until you have enough balance to pay the transaction fee cost, you cannot set a username. Please try again later.'
          )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
            error: error.message,
          })}`,
        })
        openErrorModal()
        submitting.current = false
        return
      }
    }

    // Open Confirm Identity Modal
    open()
  }, [
    t,
    address,
    balanceId,
    errorMessages.idBotConnection,
    errorMessages.idBotRateLimit,
    errorMessages.rpcIdentityConnection,
    open,
    openErrorModal,
  ])

  const submitPremium = useCallback(async () => {
    submitting.current = true
    let canRegister = false
    // Check status of registration
    try {
      const retval = await post(
        `${ID_BOT}/status`,
        `{ "address": "${address}", "handle": "${normalize(identityUsername)}" }`
      )
      if (retval?.address === '') {
        canRegister = true
      }
      if (retval?.address !== '' && retval?.address !== address) {
        setMessage(
          t(
            'components.identity.usernameBeingClaimed',
            'Username is pending registration with another user'
          )
        )
        setBorderStatus('error')
        submitting.current = false
        return
      }
      if (retval?.expiry) {
        setExpiryTimestamp(convertToEpochSeconds(retval.expiry))
      }
    } catch (error: any) {
      setErrorMessage({
        message: errorMessages.idBotConnection,
        details: `${t(
          'components.identity.errorMessagePremium',
          'The Lamina1 ID Bot is used to check and hold (for 10 minutes) your username before you complete its purchase. This helps prevent front running. Please try again later.'
        )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
          error: error.message,
        })}`,
      })
      openErrorModal()
      submitting.current = false
      return
    }

    if (canRegister) {
      // Reserve username on ID Bot and get timer
      try {
        const retval = await post(
          `${ID_BOT}/register`,
          JSON.stringify({ address, handle: normalize(identityUsername), renewal: isRenewal })
        )
        if (retval?.expiry) {
          setExpiryTimestamp(convertToEpochSeconds(retval.expiry))
        }
        if (!retval?.success) {
          setMessage(
            t('components.identity.usernameJustClaimed', 'Username is no longer available')
          )
          setBorderStatus('error')
          submitting.current = false
          return
        }
      } catch (error: any) {
        setErrorMessage({
          message: errorMessages.idBotConnection,
          details: `${t(
            'components.identity.errorMessagePremium',
            'The Lamina1 ID Bot is used to check and hold (for 10 minutes) your username before you complete its purchase. This helps prevent front running. Please try again later.'
          )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
            error: error.message,
          })}`,
        })
        openErrorModal()
        submitting.current = false
        return
      }
    }
    setIsTimerOn(true)

    // Open Confirm Identity Modal
    open()
  }, [address, errorMessages.idBotConnection, identityUsername, open, openErrorModal, t, isRenewal])

  const handleSubmit = (submit: () => Promise<void>) => {
    setIsConfirming(true)
    submit().then(() => {
      setIsConfirming(false)
    })
  }

  // Check error messages on username input
  const setErrorMessages = useCallback(() => {
    setMessage('')
    setDisplayUsernameRules(false)

    // Check if username follows ENSIP-15 rules
    try {
      normalize(identityUsername)
    } catch (error) {
      setDisplayUsernameRules(true)
      setMessage(t('components.identity.userValidation.invalid'))
      setBorderStatus('error')
      return
    }

    // Check if identity has dots in it
    if (identityUsername.includes('.')) {
      setDisplayUsernameRules(true)
      setMessage(
        t('components.identity.userValidation.noDotsError', 'Username cannot contain dots')
      )
      setBorderStatus('error')
      return
    }

    // If no errors, set success message
    usernameAvailable.current = true
  }, [identityUsername, t])

  // Check availability of username
  const checkAvailabilityFree = useCallback(() => {
    setTested(identityUsername)
    setErrorMessages()
    if (!usernameAvailable.current) {
      return
    }
    setMessage(t('components.identity.usernameAvailable', 'Username is available!'))
    setBorderStatus('success')
  }, [identityUsername, setErrorMessages, t])

  // Check availability of username
  const checkAvailabilityPremium = useCallback(async () => {
    // Trigger changes on input
    setTested(identityUsername)

    // Set error messages
    setErrorMessages()
    if (!usernameAvailable.current) {
      return
    }

    // Check with ID Bot if username is valid
    try {
      const retval = await post(
        `${ID_BOT}/check`,
        JSON.stringify({ handle: normalize(identityUsername) })
      )
      if (!retval?.valid) {
        usernameAvailable.current = false
        setDisplayUsernameRules(true)
        setMessage(t('components.identity.userValidation.invalid'))
        setBorderStatus('error')
        return
      }
    } catch (error: any) {
      usernameAvailable.current = false
      setErrorMessage({
        message: errorMessages.idBotConnection,
        details: `${t(
          'components.identity.errorMessagePremium',
          'The Lamina1 ID Bot is used to check and hold (for 10 minutes) your username before you complete its purchase. This helps prevent front running. Please try again later.'
        )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
          error: error.message,
        })}`,
      })
      openErrorModal()
      return
    }

    // Check if username is available on chain
    try {
      const available = await confirmHandleAvailability(normalize(identityUsername))
      if (available) {
        usernameAvailable.current = true
        setMessage(t('components.identity.usernameAvailable'))
        setBorderStatus('success')
      } else {
        usernameAvailable.current = false
        setMessage(t('components.identity.usernameNotAvailable', 'Username is not available'))
        setBorderStatus('error')
        return
      }
    } catch (error: any) {
      usernameAvailable.current = false
      setErrorMessage({
        message: errorMessages.rpcIdentityConnection,
        details: `${t(
          'components.identity.errorMessageCheckOnChain',
          'To check if your username is available, we need to read from the Lamina1 blockchain. Please try again later.'
        )}\n${t('components.identity.errorDetails', 'Error details: {{error}}', {
          error: error.message,
        })}`,
      })
      openErrorModal()
      return
    }

    // Check if user has enough balance
    enoughBalance.current = await checkBalance()
  }, [
    checkBalance,
    errorMessages.rpcIdentityConnection,
    identityUsername,
    openErrorModal,
    setErrorMessages,
    t,
  ])

  // Check availability for renewal (always available)
  const checkAvailabilityRenewal = useCallback(async () => {
    usernameAvailable.current = true
    setMessage(t('components.identity.usernameAvailable'))
    setBorderStatus('success')
    // Check if user has enough balance
    enoughBalance.current = await checkBalance()
  }, [checkBalance])

  const handleCheckAvailability = (check: () => Promise<void>) => {
    setIsChecking(true)
    check().then(() => {
      setIsChecking(false)
    })
  }

  // Define icon left display on username input
  const iconLeft = useCallback(() => {
    if (tested && !usernameAvailable.current) {
      return <InvalidIcon />
    }
    if (tested && message) {
      return <CheckedIcon />
    }
    return <AtIcon />
  }, [tested, message])

  // Process username input
  const onChangeIdentityUsername = useCallback(
    (value: string) => {
      setIdentityUsername(value.toLowerCase())
      setCountChars(value.length)
      setTested('')
      setIsTimerOn(false)
      setBorderStatus('default')
      setMessage(label)
      if (isAddress(value)) {
        setMessage(
          t(
            'components.identity.userValidation.address',
            'You cannot make a username that looks like an account address. Please adjust your username.'
          )
        )
        setBorderStatus('error')
      }
      if (value.length < MIN_HANDLE_LENGTH) {
        setMessage(t('components.identity.userValidation.short', { count: MIN_HANDLE_LENGTH }))
        setBorderStatus('error')
      }
      if (value.length > MAX_HANDLE_LENGTH - 4) {
        setMessage(t('components.identity.userValidation.long', { count: MAX_HANDLE_LENGTH - 4 }))
        setBorderStatus('error')
      }
      usernameAvailable.current = false
    },
    [label, t]
  )

  // if username already setup redirect to premium username
  useEffect(() => {
    if (state) {
      if ((state.pathname === '/profile' || state.pathname === '/wallet') && !state.username) {
        navigate('/freeusername')
      }
    } else navigate('/premiumusername')
    if (selectedUsername) {
      onChangeIdentityUsername(selectedUsername)
    }
  }, [navigate, premium, selectedUsername])

  return (
    <Stack style={{ marginLeft: '5px', marginRight: '5px' }}>
      {address && (
        <ModalSkeleton
          opened={opened}
          onClose={closeModal}
          title={isConfirmed ? '' : premium ? premiumModalTitle : freeModalTitle}
        >
          {premium ? (
            minBalance && (
              <ConfirmPremiumUsername
                close={closeModal}
                success={success}
                address={address}
                handle={identityUsername}
                handleConfirmation={handleConfirmation}
                price={minBalance}
                isRenewal={isRenewal}
              />
            )
          ) : (
            <ConfirmFreeUsername
              close={closeModal}
              success={success}
              address={address}
              handle={identityUsername}
              handleConfirmation={setIsConfirmed}
            />
          )}
        </ModalSkeleton>
      )}
      <Modal
        opened={openedErrorModal}
        onClose={closeErrorModal}
        size="lg"
        lockScroll={false}
        style={{ header: { margin: '0 1.6px' } }}
      >
        <ErrorMessage
          message={errorMessage.message}
          details={errorMessage.details}
          report={errorMessage.report}
        />
      </Modal>
      <Stack className={classes.header} style={{ gap: '32px' }}>
        {premium && isTimerOn && <Timer expiryTimestamp={expiryTimestamp} onTimeout={timeout} />}
      </Stack>
      <Grid>
        <Grid.Col
          span={{
            xs: 0,
            md: 12,
            lg: 6,
          }}
          className={classes.panel}
        >
          <Lamina1Logo alwaysWhite />
          <img
            className={classes.graphic}
            src={premium ? identitygif : profileSetup}
            alt="identity"
          />
        </Grid.Col>
        <Grid.Col
          span={{
            xs: 12,
            md: 12,
            lg: 6,
          }}
          className={classes.panelInfo}
        >
          <Stack className={classes.info}>
            {isUserSettings && <BackButton />}
            <Title className={classes.titleArea}>
              {premium
                ? premiumTitleArea
                : createProfile
                  ? t('components.identity.createProfile', 'Create Your Profile')
                  : freeTitleArea}
            </Title>
            {premium ? (
              <PremiumTextArea className={classes.textArea} renewal={isRenewal} />
            ) : (
              <FreeTextArea className={classes.textArea} createProfile={createProfile} />
            )}
            {createProfile && address && (
              <Stack style={{ gap: '30px' }}>
                <AddressDisplay
                  address={address}
                  showIdentity={false}
                  truncated={isMobile}
                  copyButton={false}
                />
                <Text style={{ fontSize: 18 }}>
                  {t(
                    'components.identity.createProfileDescription',
                    'Start personalizing this Profile by setting a username.'
                  )}
                </Text>
                <Stack style={{ display: 'flex', flexDirection: 'row' }}>
                  <RootButton secondary expand onClick={skip}>
                    {t('buttons.skip', 'Skip')}
                  </RootButton>

                  <RootButton onClick={setAUsername} expand>
                    {t('buttons.setAUsername', 'Set a Username')}
                  </RootButton>
                </Stack>
              </Stack>
            )}
            {!createProfile && (
              <Stack>
                {userIdentity?.username && !isRenewal && (
                  <Stack className={isMobile ? classes.usernameBoxMobile : classes.usernameBox}>
                    <Text className={classes.username}>
                      {t('components.identity.currentUsername', 'Current Username')}:
                    </Text>
                    <Text className={classes.usernameTag}>
                      {userIdentity.username ? `@${userIdentity.username}` : ''}
                    </Text>
                  </Stack>
                )}
                <SpecialInputTile
                  label={label}
                  iconLeft={iconLeft()}
                  value={identityUsername}
                  borderStatus={borderStatus}
                  placeholder={t('components.identity.usernamePlaceholder', 'your-username')}
                  rule={<IdentityStringSizeRule countChars={countChars} />}
                  errorMessage={message}
                  success={borderStatus === 'success'}
                  onChange={e => onChangeIdentityUsername(e.target.value)}
                  disabled={!!selectedUsername}
                />
                {premium && (
                  <PremiumDetails
                    detailsBox={classes.detailsBox}
                    details={classes.details}
                    minBalance={minBalance}
                    displayBalance={displayBalance}
                    enoughBalance={enoughBalance.current}
                  />
                )}
                {displayUsernameRules && <IdentityNameRules />}
                <Stack style={{ display: 'flex', flexDirection: 'row' }}>
                  {isUserSettings ? null : (
                    <RootButton secondary expand onClick={skip}>
                      {t('buttons.skip', 'Skip')}
                    </RootButton>
                  )}
                  {usernameAvailable.current ? (
                    <RootButton
                      disabled={
                        premium ? !enoughBalance.current || submitting.current : submitting.current
                      }
                      onClick={
                        premium ? () => handleSubmit(submitPremium) : () => handleSubmit(submitFree)
                      }
                      expand
                    >
                      {!isConfirming
                        ? t('buttons.confirm', 'Confirm')
                        : t('buttons.confirming', 'Confirming...')}
                    </RootButton>
                  ) : (
                    <RootButton
                      disabled={
                        !identityUsername ||
                        countChars > MAX_HANDLE_LENGTH - 4 ||
                        borderStatus === 'error' ||
                        isChecking
                      }
                      onClick={
                        premium
                          ? isRenewal
                            ? checkAvailabilityRenewal
                            : () => handleCheckAvailability(checkAvailabilityPremium)
                          : checkAvailabilityFree
                      }
                      expand
                    >
                      {premium
                        ? isChecking
                          ? t('buttons.checking', 'Checking...')
                          : premiumCheckAvailabilityButtonText
                        : freeCheckAvailabilityButtonText}
                    </RootButton>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

Component.displayName = 'Username'

// eslint-disable-next-line import/prefer-default-export
export { Component }
